import React, { useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FeatureFlagKey } from 'features/DeveloperTools/config/constants';
import { useFeatureFlags } from 'features/DeveloperTools/hooks/useFeatureFlags';
import { Modal } from 'components/Modal/Modal';
import { Button } from 'components/Button/Button';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '800px',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const FlagItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const StyledModalContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const StyledFlagList = styled(List)({
  padding: 0,
});

const StyledBadgeChip = styled(Chip)({
  height: 20,
  fontSize: '0.7rem',
});

interface FeatureFlagsProps {
  open: boolean;
  onClose: () => void;
}

export const FeatureFlags = ({ open, onClose }: FeatureFlagsProps) => {
  const {
    getAllFlags,
    toggleFlag,
    clearFlags,
    getEnvValue,
    getCurrentValue,
    isBooleanEnabled,
    hasLocalStorageOverride,
    isEnvControlled,
    getCurrentCobrands,
    addCobrands,
    removeCobrand,
    getHelperText,
  } = useFeatureFlags();
  const allFlags = getAllFlags;
  const [cobrandInputs, setCobrandInputs] = useState<Record<string, string>>({});

  const handleToggle = (key: FeatureFlagKey) => {
    toggleFlag(key);
    setCobrandInputs((prev) => ({ ...prev, [key]: '' }));
  };

  const handleCobrandChange = (key: FeatureFlagKey, value: string) => {
    setCobrandInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleCobrandSubmit = (key: FeatureFlagKey) => {
    const newCobrandValue = cobrandInputs[key]?.trim();
    if (newCobrandValue) {
      addCobrands(key, newCobrandValue);
      setCobrandInputs((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const handleCobrandKeyPress = (key: FeatureFlagKey, event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleCobrandSubmit(key);
    }
  };

  const handleClear = () => {
    clearFlags();
    setCobrandInputs({});
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      showHeader
      modalTitle="Feature Flags"
      modalTitleId="feature-flags-title"
    >
      <StyledModalContent>
        <StyledPaper elevation={3}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Toggle features on/off for local testing. Changes are saved to localStorage and
                <strong> override CI/CD values</strong> in non-production environments.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Boolean mode:</strong> Use the toggle to enable/disable for all cobrands.
                <br />
                <strong>Cobrand mode:</strong> Enter comma-separated cobrands (e.g., hphc,medica) to
                enable for specific cobrands only.
              </Typography>
            </Box>

            <Button variant="outlined" color="error" onClick={handleClear} size="small" fullWidth>
              Clear All
            </Button>

            <StyledFlagList>
              {allFlags.map((flag) => {
                const envControlled = isEnvControlled(flag.key as FeatureFlagKey);
                const envValue = getEnvValue(flag.key as FeatureFlagKey);
                const currentValue = getCurrentValue(flag.key as FeatureFlagKey);
                const cobrandInput = cobrandInputs[flag.key] || '';

                return (
                  <FlagItem key={flag.key} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    {/* Top line: title (left) + chips + toggle (right) */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      gap={1}
                    >
                      {/* Line 1: title */}
                      <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                        {flag.key}
                      </Typography>
                    </Box>
                    {/* Line 2: chips + toggle */}
                    <Box mt={0.5} display="flex" alignItems="center" gap={1} width="100%">
                      <StyledBadgeChip
                        label={currentValue}
                        size="small"
                        color={flag.enabled ? 'success' : 'default'}
                        variant="outlined"
                      />
                      {envControlled && (
                        <StyledBadgeChip
                          label="ENV"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      <Switch
                        checked={isBooleanEnabled(flag.key as FeatureFlagKey)}
                        onChange={() => handleToggle(flag.key as FeatureFlagKey)}
                        inputProps={{ 'aria-label': `Toggle ${flag.key}` }}
                      />
                    </Box>

                    {/* Line 3: description */}
                    <Box mt={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        {flag.description}
                      </Typography>
                      {envValue && (
                        <Typography
                          component="div"
                          variant="caption"
                          color="info.main"
                          sx={{ mt: 0.5 }}
                        >
                          CI/CD Value: <strong>{envValue}</strong>{' '}
                          {hasLocalStorageOverride(flag.key as FeatureFlagKey) ? (
                            <Typography
                              component="span"
                              variant="caption"
                              color="warning.main"
                              sx={{ fontWeight: 'bold' }}
                            >
                              (overridden by localStorage)
                            </Typography>
                          ) : (
                            '(can be overridden below)'
                          )}
                        </Typography>
                      )}
                    </Box>

                    {/* Current cobrands as chips */}
                    {(() => {
                      const currentCobrands = getCurrentCobrands(flag.key as FeatureFlagKey);
                      return currentCobrands.length > 0 ? (
                        <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                          {currentCobrands.map((cobrand) => (
                            <Chip
                              key={cobrand}
                              label={cobrand}
                              size="small"
                              onDelete={() => removeCobrand(flag.key as FeatureFlagKey, cobrand)}
                              color="primary"
                              variant="filled"
                            />
                          ))}
                        </Box>
                      ) : null;
                    })()}

                    {/* Cobrand input field */}
                    <Box mt={2}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Add cobrands (e.g., hphc,medica,bcbskc)"
                        value={cobrandInput}
                        onChange={(e) =>
                          handleCobrandChange(flag.key as FeatureFlagKey, e.target.value)
                        }
                        onKeyPress={(e) => handleCobrandKeyPress(flag.key as FeatureFlagKey, e)}
                        label="Add Cobrands (optional)"
                        disabled={isBooleanEnabled(flag.key as FeatureFlagKey)}
                        helperText={getHelperText(flag.key as FeatureFlagKey, envValue)}
                        sx={{ backgroundColor: 'background.paper' }}
                        InputProps={{
                          endAdornment: cobrandInput && (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="add cobrands"
                                onClick={() => handleCobrandSubmit(flag.key as FeatureFlagKey)}
                                edge="end"
                                size="small"
                                color="primary"
                              >
                                <AddIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </FlagItem>
                );
              })}
            </StyledFlagList>

            {allFlags.length === 0 && (
              <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
                No feature flags configured
              </Typography>
            )}
          </Stack>
        </StyledPaper>
      </StyledModalContent>
    </Modal>
  );
};
